import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Printer, X, Loader2, Check, AlertCircle, Wifi, WifiOff, Settings2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../LanguageContext';

/**
 * PrintConfigModal — full LumaBooth-style print dialog.
 *
 * Connects to the print server through the site's WebSocket relay, lists the
 * printers attached to that PC, lets the operator pick paper size + copies +
 * printer, and sends the job.
 *
 * Why a relay instead of talking to the printer PC directly: this page is served
 * over https://, and browsers block a plain ws:// socket from a secure page
 * (mixed content). The old build dialled ws://<LAN-IP>:8765 and the browser
 * killed it before a packet left the device — printing could never connect. Both
 * ends now dial out to wss://<this host>/print-relay and pair on a shared code,
 * which also means the printer no longer has to be on the same WiFi.
 *
 * Props:
 *   isOpen, onClose
 *   imageData  — data: URL, http(s) URL, or blob: URL of the image to print
 */
const RELAY_URL = process.env.REACT_APP_PRINT_RELAY_URL ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/print-relay`
    : '');

const normalizeCode = (raw) => String(raw || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

// The print server needs the image bytes, not a reference to them. A blob: URL
// resolves only inside this tab and an http URL may sit behind auth, so resolve
// whatever we were handed down to a data: URL before sending.
async function toDataUrl(image) {
  const value = String(image || '');
  if (!value) throw new Error('No image to print');
  if (value.startsWith('data:')) return value;

  const res = await fetch(value);
  if (!res.ok) throw new Error(`Could not read the image (${res.status})`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read the image'));
    reader.readAsDataURL(blob);
  });
}

export default function PrintConfigModal({ isOpen, onClose, imageData }) {
  const { language } = useLanguage();
  const wsRef = useRef(null);

  const [pairCode, setPairCode] = useState(
    () => localStorage.getItem('photobooth_print_pair_code') ||
          process.env.REACT_APP_PRINT_RELAY_CODE || ''
  );
  const [editingCode, setEditingCode] = useState(false);
  const [printerOnline, setPrinterOnline] = useState(false);
  const [connectionState, setConnectionState] = useState('idle'); // idle | connecting | connected | error
  const [printers, setPrinters] = useState([]);
  const [paperSizes, setPaperSizes] = useState([
    { id: '4x6',   name: '4×6 inch (10×15 cm)' },
    { id: '5x7',   name: '5×7 inch (13×18 cm)' },
    { id: '6x8',   name: '6×8 inch (15×20 cm)' },
    { id: 'a4',    name: 'A4 (210×297 mm)' },
    { id: 'letter', name: 'Letter (8.5×11 inch)' },
  ]);

  // Remembered across photos AND across reloads: the operator picks the printer
  // once when setting up the booth, not once per guest.
  const [selectedPrinter, setSelectedPrinter] = useState(
    () => localStorage.getItem('photobooth_printer') || ''
  );
  const [selectedPaper, setSelectedPaper] = useState(
    () => localStorage.getItem('photobooth_paper') || '4x6'
  );
  const [copies, setCopies] = useState(() => {
    const n = parseInt(localStorage.getItem('photobooth_copies'), 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  const rememberPrinter = (name) => {
    setSelectedPrinter(name);
    try { localStorage.setItem('photobooth_printer', name); } catch { /* private mode */ }
  };
  const rememberPaper = (id) => {
    setSelectedPaper(id);
    try { localStorage.setItem('photobooth_paper', id); } catch { /* private mode */ }
  };
  const rememberCopies = (n) => {
    setCopies(n);
    try { localStorage.setItem('photobooth_copies', String(n)); } catch { /* private mode */ }
  };
  const [showWindowsDialog, setShowWindowsDialog] = useState(false);
  const [changingPrinter, setChangingPrinter] = useState(false);
  // 'auto'   — the photo prints the moment it reaches the printer PC
  // 'manual' — it is only saved there; the operator prints from the local gallery
  const [printMode, setPrintMode] = useState('auto');
  const [savedCount, setSavedCount] = useState(0);

  const [printState, setPrintState] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  // Connect on open
  useEffect(() => {
    if (!isOpen) return;
    const code = normalizeCode(pairCode);
    if (code.length < 6 || !RELAY_URL) return;

    setConnectionState('connecting');
    setPrinterOnline(false);
    let ws;
    try {
      ws = new WebSocket(`${RELAY_URL}?role=tablet&code=${encodeURIComponent(code)}`);
    } catch {
      setConnectionState('error');
      return;
    }
    wsRef.current = ws;

    // `connectionState` is captured by this closure, so reading it here would
    // always see the value from the render that started the connection. Track
    // the outcome in a local flag instead.
    let settled = false;
    const connectTimeout = setTimeout(() => {
      if (!settled) {
        setConnectionState('error');
        try { ws.close(); } catch { /* already closing */ }
      }
    }, 10000);

    ws.onopen = () => {
      settled = true;
      clearTimeout(connectTimeout);
      setConnectionState('connected');
      ws.send(JSON.stringify({ type: 'getPrinters' }));
    };

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }

      // The relay reports whether the printer PC is actually dialled in — being
      // connected to the relay says nothing about the printer being reachable.
      if (msg.type === 'relayStatus') {
        setPrinterOnline(!!msg.printerOnline);
        if (msg.printerOnline && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'getPrinters' }));
        }
        return;
      }

      if (msg.type === 'printerList') {
        setPrinterOnline(true);
        setPrinters(msg.printers || []);
        if (msg.paperSizes?.length) setPaperSizes(msg.paperSizes);
        if (msg.defaultPaper && !localStorage.getItem('photobooth_paper')) setSelectedPaper(msg.defaultPaper);
        if (msg.defaultMode === 'auto' || msg.defaultMode === 'manual') setPrintMode(msg.defaultMode);
        if (typeof msg.savedCount === 'number') setSavedCount(msg.savedCount);
        // Keep the remembered printer whenever it is still attached to that PC;
        // only fall back to auto-selection the first time, or if it vanished.
        const available = msg.printers || [];
        const remembered = localStorage.getItem('photobooth_printer');
        const stillThere = remembered && available.some(p => p.name === remembered);
        if (stillThere) {
          setSelectedPrinter(remembered);
        } else {
          const photo = available.find(p => p.isPhotoPrinter);
          const chosen = msg.defaultPrinter || photo?.name || available[0]?.name || '';
          rememberPrinter(chosen);
        }
      }

      if (msg.type === 'printStatus') {
        if (msg.status === 'processing') setPrintState('sending');
        else if (msg.status === 'success') {
          setPrintState('success');
          setTimeout(() => { onClose(); setPrintState('idle'); }, 1800);
        } else if (msg.status === 'error') {
          setPrintState('error');
          setErrorMsg(msg.error || 'Unknown error');
        }
      }
    };

    ws.onerror = () => { settled = true; clearTimeout(connectTimeout); setConnectionState('error'); };
    ws.onclose  = () => {
      clearTimeout(connectTimeout);
      setPrinterOnline(false);
      // Only an already-established socket dropping is worth reporting; a close
      // during connect is handled by the timeout/error paths above.
      setConnectionState((prev) => (prev === 'connected' ? 'error' : prev));
    };

    return () => {
      clearTimeout(connectTimeout);
      ws.onclose = null; // unmount is not a connection failure
      try { ws.close(); } catch { /* noop */ }
    };
  }, [isOpen, pairCode]); // eslint-disable-line

  const handlePrint = async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    setPrintState('sending');
    setErrorMsg('');

    let payload;
    try {
      payload = await toDataUrl(imageData);
    } catch (err) {
      setPrintState('error');
      setErrorMsg(err.message);
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setPrintState('error');
      setErrorMsg(language === 'he' ? 'החיבור נותק' : 'Connection lost');
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'print',
      imageData: payload,
      mode: printMode,
      printerName: selectedPrinter,
      paperSize: selectedPaper,
      copies,
      showDialog: showWindowsDialog,
    }));
  };

  const savePairCode = (raw) => {
    const code = normalizeCode(raw);
    setPairCode(code);
    localStorage.setItem('photobooth_print_pair_code', code);
    setEditingCode(false);
    setConnectionState('idle');
  };

  if (!isOpen) return null;

  const isHe = language === 'he';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center">
                  <Printer className="w-5 h-5 text-green-400" />
                </div>
                <h2 className="text-xl font-bold text-white">
                  {isHe ? 'הדפסה' : 'Print'}
                </h2>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Pairing code + connection status */}
            <div className={`mb-5 rounded-xl border p-3 ${
              connectionState === 'connected' && printerOnline ? 'bg-green-500/5 border-green-500/30' :
              connectionState === 'error'                      ? 'bg-red-500/5 border-red-500/30' :
                                                                 'bg-slate-800/40 border-slate-700'
            }`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {connectionState === 'connected' && printerOnline && <Wifi className="w-4 h-4 text-green-400 shrink-0" />}
                  {connectionState === 'connecting' && <Loader2 className="w-4 h-4 text-yellow-400 animate-spin shrink-0" />}
                  {(connectionState === 'error' || connectionState === 'idle' ||
                    (connectionState === 'connected' && !printerOnline)) && <WifiOff className="w-4 h-4 text-red-400 shrink-0" />}
                  {editingCode ? (
                    <input
                      type="text"
                      defaultValue={pairCode}
                      placeholder={isHe ? 'קוד חיבור' : 'Pairing code'}
                      autoFocus
                      autoCapitalize="characters"
                      onKeyDown={(e) => e.key === 'Enter' && savePairCode(e.target.value)}
                      onBlur={(e) => savePairCode(e.target.value)}
                      className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-sm font-mono tracking-widest uppercase"
                    />
                  ) : (
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-400">
                        {isHe ? 'קוד חיבור למדפסת' : 'Printer pairing code'}
                      </p>
                      <p className="text-sm text-white font-mono tracking-widest truncate">
                        {pairCode || (isHe ? 'לא מוגדר' : 'not configured')}
                      </p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setEditingCode(true)}
                  className="text-xs text-violet-400 hover:text-violet-300 shrink-0"
                >
                  {isHe ? 'שנה' : 'Change'}
                </button>
              </div>

              {normalizeCode(pairCode).length < 6 && !editingCode && (
                <p className="text-xs text-slate-400 mt-2 flex items-start gap-1">
                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                  {isHe
                    ? 'הזן את הקוד שמוצג בחלון תוכנת ההדפסה במחשב המחובר למדפסת (לפחות 6 תווים).'
                    : 'Enter the code shown in the print server window on the printer PC (at least 6 characters).'}
                </p>
              )}
              {connectionState === 'error' && (
                <p className="text-xs text-red-400 mt-2 flex items-start gap-1">
                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                  {isHe
                    ? 'לא ניתן להתחבר לשירות ההדפסה. בדוק את חיבור האינטרנט ונסה שוב.'
                    : 'Could not reach the print service. Check your internet connection and try again.'}
                </p>
              )}
              {connectionState === 'connected' && !printerOnline && (
                <p className="text-xs text-amber-400 mt-2 flex items-start gap-1">
                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                  {isHe
                    ? 'תוכנת ההדפסה לא מחוברת. הפעל את PhotoBoothPrintServer במחשב המדפסת וודא שהקוד זהה.'
                    : 'The print server is not connected. Start PhotoBoothPrintServer on the printer PC and check the code matches.'}
                </p>
              )}
              {connectionState === 'connecting' && (
                <p className="text-xs text-yellow-400 mt-2">
                  {isHe ? 'מתחבר...' : 'Connecting...'}
                </p>
              )}
            </div>

            {/* Only show controls once the printer PC is actually reachable */}
            {connectionState === 'connected' && printerOnline && (
              <>
                {/* Auto vs manual — what happens the moment the photo lands
                    on the printer PC. Either way it is saved there. */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">
                    {isHe ? 'מה לעשות עם התמונה' : 'What happens to the photo'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPrintMode('auto')}
                      className={`rounded-xl border p-3 text-start transition-all ${
                        printMode === 'auto'
                          ? 'border-violet-500 bg-violet-600/15'
                          : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                      }`}
                    >
                      <p className="text-sm font-semibold text-white">
                        {isHe ? 'הדפסה אוטומטית' : 'Print automatically'}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                        {isHe ? 'יוצא מהמדפסת מיד' : 'Comes out of the printer right away'}
                      </p>
                    </button>
                    <button
                      onClick={() => setPrintMode('manual')}
                      className={`rounded-xl border p-3 text-start transition-all ${
                        printMode === 'manual'
                          ? 'border-violet-500 bg-violet-600/15'
                          : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                      }`}
                    >
                      <p className="text-sm font-semibold text-white">
                        {isHe ? 'שמירה למחשב' : 'Save to the PC'}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                        {isHe ? 'תדפיס ידנית מהגלריה' : 'Print by hand from the gallery'}
                      </p>
                    </button>
                  </div>
                  {printMode === 'manual' && (
                    <p className="text-[11px] text-slate-400 mt-2 leading-snug">
                      {isHe
                        ? `במחשב המדפסת פתח http://localhost:8765/ כדי לראות את התמונות ולהדפיס.${savedCount ? ` כרגע שמורות שם ${savedCount}.` : ''}`
                        : `On the printer PC open http://localhost:8765/ to view the photos and print.${savedCount ? ` ${savedCount} saved there so far.` : ''}`}
                    </p>
                  )}
                </div>

                {/* Printer picker — collapsed once one is remembered. The booth
                    operator sets this up once per event; for every guest after
                    that the only question that matters is how many copies. */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {isHe ? 'מדפסת' : 'Printer'}
                    </label>
                    {selectedPrinter && !changingPrinter && (
                      <button
                        onClick={() => setChangingPrinter(true)}
                        className="text-xs text-violet-400 hover:text-violet-300"
                      >
                        {isHe ? 'שנה' : 'Change'}
                      </button>
                    )}
                  </div>
                  {selectedPrinter && !changingPrinter ? (
                    <p className="text-sm text-white truncate bg-slate-800/60 rounded-lg px-3 py-2.5 border border-slate-700">
                      🖨️ {selectedPrinter}
                    </p>
                  ) : printers.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">
                      {isHe ? 'לא נמצאו מדפסות' : 'No printers found'}
                    </p>
                  ) : (
                    <select
                      value={selectedPrinter}
                      onChange={(e) => { rememberPrinter(e.target.value); setChangingPrinter(false); }}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white"
                    >
                      {printers.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.isPhotoPrinter ? '📸 ' : '🖨️ '} {p.name}
                          {p.isDNP ? ' (DNP)' : p.isMitsubishi ? ' (Mitsubishi)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Paper size */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">
                    {isHe ? 'גודל נייר' : 'Paper Size'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {paperSizes.slice(0, 4).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => rememberPaper(p.id)}
                        className={`p-3 rounded-lg border-2 text-sm transition-all ${
                          selectedPaper === p.id
                            ? 'border-violet-500 bg-violet-500/20 text-white'
                            : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-500'
                        }`}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                  {paperSizes.length > 4 && (
                    <select
                      value={paperSizes.slice(0,4).find(p=>p.id===selectedPaper) ? '' : selectedPaper}
                      onChange={(e) => e.target.value && rememberPaper(e.target.value)}
                      className="w-full mt-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="">{isHe ? '— גדלים נוספים —' : '— More sizes —'}</option>
                      {paperSizes.slice(4).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Copies */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">
                    {isHe ? 'מספר עותקים' : 'Copies'}
                  </label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => rememberCopies(Math.max(1, copies - 1))}
                      className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 text-white text-lg hover:bg-slate-700">−</button>
                    <span className="text-2xl font-bold text-white w-12 text-center">{copies}</span>
                    <button onClick={() => rememberCopies(Math.min(20, copies + 1))}
                      className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 text-white text-lg hover:bg-slate-700">+</button>
                  </div>
                </div>

                {/* Advanced: Windows dialog */}
                <label className="flex items-center gap-2 mb-5 text-sm text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showWindowsDialog}
                    onChange={(e) => setShowWindowsDialog(e.target.checked)}
                    className="accent-violet-500"
                  />
                  <Settings2 className="w-4 h-4" />
                  {isHe
                    ? 'הצג דיאלוג הדפסה של Windows (לבחירת הגדרות מנהל ההתקן)'
                    : 'Show Windows print dialog (driver settings)'}
                </label>

                {/* Action */}
                <Button
                  onClick={handlePrint}
                  disabled={printState === 'sending' || !selectedPrinter}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-base"
                >
                  {printState === 'sending' ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" />{isHe ? 'שולח להדפסה...' : 'Sending...'}</>
                  ) : printState === 'success' ? (
                    <><Check className="w-5 h-5 mr-2" />{isHe ? 'נשלח בהצלחה!' : 'Sent successfully!'}</>
                  ) : (
                    <><Printer className="w-5 h-5 mr-2" />{isHe ? `הדפס ${copies > 1 ? `(${copies} עותקים)` : ''}` : `Print ${copies > 1 ? `(${copies} copies)` : ''}`}</>
                  )}
                </Button>

                {printState === 'error' && (
                  <p className="text-xs text-red-400 mt-3 text-center">
                    <AlertCircle className="w-3 h-3 inline mr-1" />{errorMsg}
                  </p>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
